import React, { useState, useEffect } from "react";
import "./Products.css";
import ContactSection from "../components/Contact/contact_homepage";

// ✅ Base URLs (auto fallback for local and production)
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://threegor-1.onrender.com";
const UPLOADS_URL =
  process.env.REACT_APP_UPLOADS_URL || "https://threegor-1.onrender.com/"; // for images

// --- API FETCH FUNCTIONS ---
const handleApiResponse = async (response) => {
  if (!response.ok) {
    throw new Error("Failed to fetch products");
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data.data || [];
};

// ✅ Corrected endpoints
const fetchAllProducts = async () => {
  const url = `${API_BASE_URL}/api/products`;
  const response = await fetch(url);
  return handleApiResponse(response);
};

const fetchProductsByCategory = async (category) => {
  const url = `${API_BASE_URL}/api/products/category/${category}`;
  const response = await fetch(url);
  return handleApiResponse(response);
};

// ------------------------------------------------------------------

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    message: "",
  });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  const FACEBOOK_PAGE_ID = "100064243770130";

  // ✅ Updated categories to match Portfolio page
  const categories = [
    { id: "all", name: "ทั้งหมด" },
    { id: "kitchen", name: "ชุดครัว" },
    { id: "tv-wall", name: "ผนังทีวี" },
    { id: "closet", name: "ตู้เสื้อผ้า" }
  ];

  const handleImageError = (e) => {
    e.target.onError = null;
    e.target.src =
      "https://via.placeholder.com/600x400?text=Image+Not+Found";
    e.target.style.filter = "grayscale(100%)";
  };

  // ✅ Fix: ensure all images point to backend static folder
  const resolveImageUrl = (path) => {
    if (!path) return "https://via.placeholder.com/600x400?text=No+Image";
    if (path.startsWith("http")) return path;
    return `${UPLOADS_URL}${path.replace(/\\/g, "/")}`;
  };

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const data =
          selectedCategory === "all"
            ? await fetchAllProducts()
            : await fetchProductsByCategory(selectedCategory);

        setProducts(data || []);
      } catch (error) {
        console.error("Error loading products:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [selectedCategory]);

  const openModal = (product) => setSelectedProduct(product);
  const closeModal = () => setSelectedProduct(null);

  const openQuoteModal = (product) => {
    setQuoteProduct(product);
    setShowQuoteModal(true);
    setQuoteSuccess(false);
    setQuoteForm({
      name: "",
      phone: "",
      email: "",
      address: "",
      message: "",
    });
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    setQuoteProduct(null);
    setQuoteSuccess(false);
  };

  const handleQuoteFormChange = (e) => {
    setQuoteForm({
      ...quoteForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteSubmitting(true);

    const fbMessage = `สวัสดีครับ/ค่ะ 👋

ผม/ดิฉัน ${quoteForm.name} สนใจสินค้า "${quoteProduct.title}"
💰 ราคา: ฿${parseInt(quoteProduct.price).toLocaleString()}

📋 ข้อมูลติดต่อ:
📱 เบอร์โทร: ${quoteForm.phone}
📧 อีเมล: ${quoteForm.email || "ไม่ระบุ"}
📍 ที่อยู่: ${quoteForm.address || "ไม่ระบุ"}

💬 ข้อความเพิ่มเติม:
${quoteForm.message || "ไม่มี"}

รบกวนติดต่อกลับด้วยครับ/ค่ะ`;

    const fbMessengerUrl = `https://m.me/${FACEBOOK_PAGE_ID}?text=${encodeURIComponent(
      fbMessage
    )}`;
    window.open(fbMessengerUrl, "_blank");

    try {
      const response = await fetch(`${API_BASE_URL}/quotes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...quoteForm,
          productId: quoteProduct._id || quoteProduct.id,
          productTitle: quoteProduct.title,
          productPrice: quoteProduct.price,
        }),
      });

      if (!response.ok) {
        console.error("Failed to save quote to database");
      }

      setQuoteSuccess(true);
      setTimeout(() => {
        closeQuoteModal();
      }, 3000);
    } catch (error) {
      console.error("Error submitting quote:", error);
      setQuoteSuccess(true);
      setTimeout(() => {
        closeQuoteModal();
      }, 3000);
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSelectedCategory("all");
    window.location.reload();
  };

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    if (categoryId !== selectedCategory) {
      setSelectedCategory(categoryId);
    }
  };

  if (loading) {
    return (
      <div className="products-page">
        <div className="products-header">
          <h1>ผลิตภัณฑ์ของเรา</h1>
          <p>เฟอร์นิเจอร์คุณภาพสูง ออกแบบเฉพาะสำหรับคุณ</p>
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "100px 20px",
            fontSize: "18px",
            color: "#666",
          }}
        >
          กำลังโหลดสินค้า...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="products-page">
        <div className="products-header">
          <h1>ผลิตภัณฑ์ของเรา</h1>
          <p>เฟอร์นิเจอร์คุณภาพสูง ออกแบบเฉพาะสำหรับคุณ</p>
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "100px 20px",
            fontSize: "18px",
            color: "#ff4757",
          }}
        >
          เกิดข้อผิดพลาด: {error}
          <br />
          <button
            onClick={handleRetry}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              background: "#EAC27F",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            ลองอีกครั้ง
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-header">
        <h1>ผลิตภัณฑ์ของเรา</h1>
        <p>เฟอร์นิเจอร์คุณภาพสูง ออกแบบเฉพาะสำหรับคุณ</p>
      </div>

      {/* ✅ Updated Filter Section - Portfolio Style */}
      <div className="products-filter">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => handleCategoryChange(category.id)}
            aria-pressed={selectedCategory === category.id}
          >
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      <div className="products-count">
        <p>แสดง {products.length} รายการ</p>
      </div>

      {products.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "100px 20px",
            fontSize: "18px",
            color: "#666",
          }}
        >
          ไม่พบสินค้าในหมวดหมู่นี้
        </div>
      ) : (
        <div className="products-grid">
          {products.map((product) => (
            <div key={product._id || product.id} className="product-card">
              {product.badge && (
                <span className={`product-badge ${product.badge.toLowerCase()}`}>
                  {product.badge}
                </span>
              )}

              <div className="product-image-gallery">
                <img
                  src={resolveImageUrl(product.image)}
                  alt={product.title}
                  onError={handleImageError}
                />
              </div>

              <div className="product-content">
                <p className="product-category">{product.category}</p>
                <h3>{product.title}</h3>
                <p className="product-description">{product.description}</p>

                <div className="product-price">
                  <span className="product-price-current">
                    ฿{parseInt(product.price).toLocaleString()}
                  </span>
                  {/* {product.originalPrice && (
                    <span className="product-price-original">
                      ฿{parseInt(product.originalPrice).toLocaleString()}
                    </span>
                  )} */}
                </div>

                <div className="product-actions">
                  <button
                    className="product-btn-primary"
                    onClick={() => openModal(product)}
                  >
                    ดูรายละเอียด
                  </button>
                  <button
                    className="product-btn-secondary"
                    onClick={() => openQuoteModal(product)}
                  >
                    ขอใบเสนอราคา
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="product-modal-overlay" onClick={closeModal}>
          <div
            className="product-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="product-modal-close" onClick={closeModal}>
              ×
            </button>

            <div className="product-modal-image">
              <img
                src={resolveImageUrl(selectedProduct.image)}
                alt={selectedProduct.title}
                onError={handleImageError}
              />
            </div>

            <div className="product-modal-body">
              <h2 className="product-modal-title">{selectedProduct.title}</h2>
              <p className="product-modal-subtitle">
                {selectedProduct.subtitle}
              </p>

              <div className="product-modal-pricing">
                <span className="current-price">
                  ฿{parseInt(selectedProduct.price).toLocaleString()}
                </span>
                {selectedProduct.originalPrice && (
                  <span className="original-price">
                    ฿{parseInt(
                      selectedProduct.originalPrice
                    ).toLocaleString()}
                  </span>
                )}
              </div>

              <div className="product-modal-divider"></div>

              <h3 className="product-modal-section-title">
                รายละเอียดสินค้า
              </h3>
              <p className="product-modal-description">
                {selectedProduct.fullDescription}
              </p>

              <div className="product-modal-divider"></div>

              <h3 className="product-modal-section-title">คุณสมบัติเด่น</h3>
              <ul className="product-modal-features">
                {selectedProduct.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>

              <div className="product-modal-divider"></div>


              <div className="product-modal-specifications">
                {Array.isArray(selectedProduct.specifications)
                  ? selectedProduct.specifications.map((spec, idx) => (
                    <div key={idx} className="specification-item">
                      <span className="spec-label">
                        {spec.label ? spec.label + ":" : ""}
                      </span>
                      <span className="spec-value">{spec.value}</span>
                    </div>
                  ))
                  : selectedProduct.specifications
                    ?.split(/\r?\n/)
                    .filter((line) => line.trim() !== "")
                    .map((line, idx) => (
                      <div key={idx} className="specification-item">
                        <span className="spec-value">{line}</span>
                      </div>
                    ))}
              </div>


              <div className="product-modal-actions">
                <button
                  className="modal-btn-primary"
                  onClick={() => {
                    closeModal();
                    openQuoteModal(selectedProduct);
                  }}
                >
                  ขอใบเสนอราคา
                </button>
                <button className="modal-btn-secondary" onClick={closeModal}>
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      {showQuoteModal && quoteProduct && (
        <div className="product-modal-overlay" onClick={closeQuoteModal}>
          <div
            className="quote-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="product-modal-close" onClick={closeQuoteModal}>
              ×
            </button>

            {quoteSuccess ? (
              <div className="quote-success">
                <div className="success-icon">✓</div>
                <h2>เปิด Messenger เรียบร้อย!</h2>
                <p style={{ fontSize: "16px", marginBottom: "15px" }}>
                  ✅ บันทึกข้อมูลเรียบร้อยแล้ว
                </p>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#EAC27F",
                    fontWeight: "bold",
                  }}
                >
                  📱 กรุณากดปุ่ม "ส่ง" ใน Messenger เพื่อส่งข้อความถึงเรา
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginTop: "10px",
                  }}
                >
                  เราจะตอบกลับโดยเร็วที่สุด
                </p>
              </div>
            ) : (
              <>
                <h2 className="quote-modal-title">ขอใบเสนอราคา</h2>
                <p className="quote-modal-subtitle">
                  กรุณากรอกข้อมูล ระบบจะเปิด Facebook Messenger พร้อมข้อความที่กรอกไว้
                </p>

                <div className="quote-product-info">
                  <img
                    src={resolveImageUrl(quoteProduct.image)}
                    alt={quoteProduct.title}
                    onError={handleImageError}
                  />
                  <div>
                    <h3>{quoteProduct.title}</h3>
                    <p className="quote-product-price">
                      ฿{parseInt(quoteProduct.price).toLocaleString()}
                    </p>
                  </div>
                </div>

                <form onSubmit={handleQuoteSubmit} className="quote-form">
                  <div className="quote-form-group">
                    <label htmlFor="name">ชื่อ-นามสกุล *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={quoteForm.name}
                      onChange={handleQuoteFormChange}
                      required
                      placeholder="กรุณากรอกชื่อ-นามสกุล"
                    />
                  </div>

                  <div className="quote-form-group">
                    <label htmlFor="phone">เบอร์โทรศัพท์ *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={quoteForm.phone}
                      onChange={handleQuoteFormChange}
                      required
                      placeholder="กรุณากรอกเบอร์โทรศัพท์"
                    />
                  </div>

                  <div className="quote-form-group">
                    <label htmlFor="email">อีเมล</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={quoteForm.email}
                      onChange={handleQuoteFormChange}
                      placeholder="กรุณากรอกอีเมล (ไม่บังคับ)"
                    />
                  </div>

                  <div className="quote-form-group">
                    <label htmlFor="address">ที่อยู่</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={quoteForm.address}
                      onChange={handleQuoteFormChange}
                      placeholder="กรุณากรอกที่อยู่ (ไม่บังคับ)"
                    />
                  </div>

                  <div className="quote-form-group">
                    <label htmlFor="message">ข้อความเพิ่มเติม</label>
                    <textarea
                      id="message"
                      name="message"
                      value={quoteForm.message}
                      onChange={handleQuoteFormChange}
                      rows="4"
                      placeholder="รายละเอียดเพิ่มเติม เช่น ขนาด สี หรือความต้องการพิเศษ"
                    ></textarea>
                  </div>

                  <div className="quote-form-actions">
                    <button
                      type="submit"
                      className="product-btn-primary"
                      disabled={quoteSubmitting}
                    >
                      {quoteSubmitting
                        ? "กำลังเปิด Messenger..."
                        : "ส่งคำขอผ่าน Messenger"}
                    </button>
                    <button
                      type="button"
                      className="product-btn-secondary"
                      onClick={closeQuoteModal}
                      disabled={quoteSubmitting}
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <div className="products-cta">
        <h2>ต้องการปรึกษาการออกแบบ?</h2>
        <p>ทีมงานมืออาชีพพร้อมให้คำปรึกษาและออกแบบตามความต้องการของคุณ</p>
        <button
          className="cta-button"
          onClick={() =>
            window.open(
              `https://www.facebook.com/messages/t/${FACEBOOK_PAGE_ID}`,
              "_blank"
            )
          }
        >
          ติดต่อเรา
        </button>
      </div>

      <ContactSection />
    </div>
  );
};

export default Products;